/**
 * Theme Configuration
 * Centralized color palette, spacing, and other design tokens
 */

// Main Brand Colors
export const BRAND_COLORS = {
  primary: {
    main: "#0a2a0d",
    green: "#0a2a0d", // primary-green referenced in Home.jsx
    light: "#17481c",
    contrast: "#003F33",
  },
  secondary: {
    main: "#16A34A",
    light: "#66CF91",
    dark: "#128B41",
  },
  status: {
    success: "#41b151",
    error: "#bf2e2e",
    warning: "#ee8137",
    info: "#4472c4",
  },
};

// Background Colors for Different Variants
export const BACKGROUND_VARIANTS = {
  light: {
    bg: "bg-green-100",
    title: "text-gray-800",
  },
  dark: {
    bg: "bg-green-900",
    contrast: "bg-green-700",
  },
};

// Worker Role Colors 
export const WORKER_ROLE_COLORS = {
  12: "bg-[#fff2cc]", // Laborer
  15: "bg-[#b5c4e3]", // Operator
  13: "bg-[#c6e0b4]", // Mason
  14: "bg-[#fce4d6]", // Carpenter
  16: "bg-[#d6fcfa]", // Semi-truck driver
  10: "font-bold", // Foreman
};

// Worker Role Colors (Darker tints) 
export const WORKER_ROLE_COLORS_DARK = {
  12: "bg-[#edc13f]", // Laborer
  15: "bg-[#1e58cf]", // Operator
  13: "bg-[#71b93e]", // Mason
  14: "bg-[#d09878]", // Carpenter
  16: "bg-[#26cdc4]", // Semi-truck driver
  10: "font-bold", // Foreman
};

// Status Colors for Shifts (from ShiftSummary.jsx)
export const SHIFT_STATUS_COLORS = {
  "Current Shift": "bg-white border-gray-200 border-l-2 border-r-2 h-[220px]",
  "Complete Shift": "bg-green-50 h-[240px]",
  "Shift (Draft)": "bg-blue-50 h-[164px]",
  Shift: "bg-blue-50 h-[164px]",
  "Missed Shift": "bg-red-50 h-[164px]",
  "No Shift": "bg-gray-100 h-[164px]",
  "No Shift Data": "bg-gray-50 h-[164px]",
  "Clocked In": "bg-yellow-50 h-[164px]",
};

// Chart Colors (from visualizations.jsx)
export const CHART_COLORS = {
  primary: "#4472c4",
  secondary: "#ee8137",
  revenue: "#4472c4",
  cost: "#ee8137",
  gross: "#a6a6a6",
  labor: "#292a63",
  equipment: "#ee8138",
  subcontractors: "#ffc000",
  materials: "#a5a5a5",
};

// Global App Styling
export const GLOBAL_STYLES = {
  backgroundColor: "#f0f0f0",
  borderColor: "#8f8f8f",
  editorBackground: "#efefef",
};

// Status Message Colors (Tailwind classes)
export const STATUS_MESSAGE_COLORS = {
  error: "bg-red-100 text-red-700",
  warning: "bg-orange-100 text-orange-700",
  info: "bg-yellow-100 text-yellow-700",
  success: "bg-green-100 text-green-700",
};

// Static Assets Configuration
export const STATIC_ASSETS = {
  LOGO_URL:
    "https://rsapp-mass-storage-prod.s3.us-west-2.amazonaws.com/static/logo_trans_fill_dark.png",
  LOADING_INDICATOR_COLOR: "#121c12",
};

// Interaction Colors for hover states and user feedback
export const INTERACTION_COLORS = {
  danger: {
    bg: "bg-red-200",
    hover: "hover:bg-red-300",
    border: "border-red-200",
  },
  success: {
    bg: "bg-green-50",
    hover: "hover:bg-green-100",
    border: "border-green-200",
  },
  neutral: {
    bg: "bg-white",
    hover: "hover:bg-gray-400",
    border: "border-gray-200",
  },
  warning: {
    bg: "bg-orange-100",
    hover: "hover:bg-orange-200",
    border: "border-orange-200",
  },
};

// Z-Index Management
export const Z_INDEX = {
  base: "z-0",
  dropdown: "z-10",
  overlay: "z-20",
  modal: "z-30",
  tooltip: "z-40",
  popover: "z-50",
};

// Animation and Transition Classes
export const ANIMATION_CONFIG = {
  pulse: "animate-pulse",
  scale: "transition-scale ease-in duration-300",
  hover: {
    scale: "hover:scale-105",
    opacity: "hover:opacity-80",
  },
  transitions: {
    default: "transition-all duration-200 ease-in-out",
    fast: "transition-all duration-100 ease-in-out",
    slow: "transition-all duration-500 ease-in-out",
  },
};

// Typography Scale
export const TYPOGRAPHY = {
  sizes: {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
    xl: "text-xl",
    "2xl": "text-2xl",
    "2xs": "text-2xs", // Custom size used in app
  },
  weights: {
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold",
    bold: "font-bold",
  },
};

// Border and Shadow Design Tokens
export const BORDERS = {
  radius: {
    sm: "rounded",
    md: "rounded-md",
    lg: "rounded-lg",
    full: "rounded-full",
  },
  width: {
    thin: "border",
    thick: "border-2",
  },
};

export const SHADOWS = {
  sm: "shadow-sm",
  default: "shadow",
  md: "shadow-md",
  lg: "shadow-lg",
  black: "shadow-black",
  drop: "drop-shadow",
};
